import React from "react";

const ManageTablesModal = ({
  open,
  onClose,
  tables,
  loading,
  onAdd,
  onEdit,
  onViewQr,
  onBulkPrint,
  qrEnabled = true,
}) => {
  if (!open) return null;

  const showQr = !!qrEnabled;
  const editColSpan = showQr ? 4 : 3;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-[92%] max-w-[760px] max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex justify-between items-start px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 rounded-t-xl shadow-md">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                  clipRule="evenodd"
                />
              </svg>
              Manage Tables
            </h2>
            <p className="text-sm text-green-100 mt-1">
              Add and manage restaurant tables
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-white hover:text-green-100 transition-colors p-1 hover:bg-white/20 rounded-lg"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="h-px bg-gray-200"></div>

        {/* Action Bar */}
        <div className="flex justify-end items-center gap-2 px-6 py-3">
          {showQr ? (
            <button
              onClick={onBulkPrint}
              className="px-4 py-2 border border-purple-600 text-purple-600 hover:bg-purple-50 font-medium rounded-lg transition-all flex items-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Bulk Print QR
            </button>
          ) : null}

          <button
            onClick={onAdd}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Table
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-200 border-t-green-600"></div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">
                    Table No
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">
                    Capacity
                  </th>
                  {showQr ? (
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">
                      View QR
                    </th>
                  ) : null}
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">
                    Edit
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {tables.length === 0 ? (
                  <tr>
                    <td colSpan={editColSpan} className="py-12 text-center">
                      <p className="text-gray-500">No tables created yet</p>
                    </td>
                  </tr>
                ) : (
                  tables.map((t) => (
                    <tr
                      key={t._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {t.tableNumber}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{t.capacity}</td>

                      {/* VIEW QR */}
                      {showQr ? (
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => onViewQr?.(t)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors group"
                            title="View QR"
                          >
                            <svg
                              className="w-5 h-5 group-hover:scale-110 transition-transform"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                        </td>
                      ) : null}

                      {/* EDIT */}
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => onEdit(t)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors group"
                          title="Edit table"
                        >
                          <svg
                            className="w-5 h-5 group-hover:scale-110 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageTablesModal;
